import { createContext, useContext, useEffect, useMemo, useState } from "react";
import CancelBookingModal from "../Components/CancelBookingModal";
import { baseUrl } from "../utilities/configurations";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utilities/request";
import { PDFDocument } from "pdf-lib";
import PropTypes from "prop-types";
import { pdfjs } from "react-pdf";
import dayjs from "dayjs";

const RequestMedicalContext = createContext();

export function useRequestMedicalContext() {
  return useContext(RequestMedicalContext);
}

export function RequestMedicalProvider(props) {
  const [diagnosticOrders, setDiagnosticOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [procedureClass, setProcedureClass] = useState([]);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadState, setLoadState] = useState("");

  // Procedure search
  const [searchProceduresResults, setSearchProceduresResults] = useState([]); //search results
  const [searchProcedures, setSearchProcedures] = useState("");

  //Mapped procedures to their procedure class
  const [searchBranchResults, setSearchBranchResults] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [mappedProcedures, setMappedProcedures] = useState();
  const [selectedClinic, setSelectedClinic] = useState();
  const [searchBranch, setSearchBranch] = useState("");

  const [selectedDates, setSelectedDates] = useState({});
  const [selectedTimes, setSelectedTimes] = useState({});

  const [displaySummaryAlert, setDisplaySummaryAlert] = useState(false);
  const [summary, setSummary] = useState([]);

  // via file upload flow
  const [selectedProcedureClass, setSelectedProcedureClass] = useState([]);
  const [files, setFiles] = useState([]);
  const [url, setUrl] = useState();

  const [month, setMonth] = useState(dayjs().format("MM"));
  const [availableDates, setAvailableDates] = useState();
  const [availableTimes, setAvailableTimes] = useState();
  const [year, setYear] = useState(dayjs().year());

  //error handlings
  const [pcLoadState, setPcLoadState] = useState("");

  const navigate = useNavigate();

  function convertToStandardTime(militaryTime) {
    const [hours, minutes] = militaryTime.split(":");
    let hoursNum = parseInt(hours, 10);
    const period = hoursNum >= 12 ? "PM" : "AM";
    hoursNum = hoursNum % 12 || 12;
    return `${hoursNum}:${minutes} ${period}`;
  }

  useEffect(() => {
    if (!selectedPolicy) {
      navigate("/request-medical-test/policy", { replace: true });
    }
  }, []);

  const handleBack = (e) => {
    setIsLoading(false);
    navigate(-1);
  };

  const handleCancel = () => {
    setSelectedPolicy(null);
    setDiagnosticOrders([]);
    setSelectedOrders([]);
    setProcedureClass([]);
    setMappedProcedures();
    setSelectedProcedures([]);
    setFiles([]);
    setUrl();
    setSelectedProcedureClass([]);
    setMonth(dayjs().format("MM"));
    setYear(dayjs().year());
    setDisplayAlert(false);
    navigate("/home", { replace: true });
  };

  const [totalFileSize, setTotalFileSize] = useState(0);

  const removeFile = (index) => {
    const updatedFiles = [...files];
    const removedFileSize = updatedFiles[index].size || 0;

    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);

    // Update the total file size
    setTotalFileSize(totalFileSize - removedFileSize);
  };

  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    let totalSize = totalFileSize;

    // Calculate the total size of new files
    const newFilesSize = newFiles.reduce((acc, file) => acc + file.size, 0);

    // Check if the total size exceeds the limit
    if (totalSize + newFilesSize > 15 * 1024 * 1024) {
      alert("Total file size exceeds 15MB limit");
      return;
    }

    // Update the total file size
    totalSize += newFilesSize;
    setTotalFileSize(totalSize);

    const fileURLs = await Promise.all(
      newFiles.map(async (file) => {
        if (file.type === "application/pdf") {
          // Convert PDF to image
          const pdfURL = URL.createObjectURL(file);
          const pdfImages = await pdfToImage(pdfURL);
          return pdfImages;
        } else {
          return URL.createObjectURL(file);
        }
      })
    );
    setFiles([...files, ...fileURLs.flat()]);
  };

  const pdfToImage = async (pdfURL) => {
    const pdf = await fetch(pdfURL).then((res) => res.arrayBuffer());
    const pdfData = new Uint8Array(pdf);

    return new Promise((resolve) => {
      const pdfBlob = new Blob([pdfData], { type: "application/pdf" });
      const blobURL = URL.createObjectURL(pdfBlob);

      const canvasArray = [];

      pdfjs.getDocument({ url: blobURL }).promise.then((pdfDocument) => {
        const numPages = pdfDocument.numPages;

        const renderPromises = Array.from(new Array(numPages), (el, index) => {
          return pdfDocument.getPage(index + 1).then((page) => {
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const renderContext = {
              canvasContext: context,
              viewport: viewport,
            };
            const renderTask = page.render(renderContext);
            return renderTask.promise.then(() => {
              canvasArray.push(canvas.toDataURL("image/jpeg"));
            });
          });
        });

        Promise.all(renderPromises).then(() => {
          resolve(canvasArray);
        });
      });
    });
  };

  const compileToPDF = async () => {
    const pdfDoc = await PDFDocument.create();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.startsWith("data:image/jpeg;base64,")) {
        // Convert base64 encoded JPEG image to bytes
        const base64Image = file.replace("data:image/jpeg;base64,", "");
        const imageBytes = Uint8Array.from(atob(base64Image), (c) =>
          c.charCodeAt(0)
        );

        // Embed JPEG image into PDF
        const image = await pdfDoc.embedJpg(imageBytes);
        const page = pdfDoc.addPage();
        const { width, height } = image.scale(1); // Get original image dimensions
        const pageWidth = page.getWidth();
        const pageHeight = page.getHeight();

        // Calculate scaling factor to fit image proportionally within the page
        const scaleX = pageWidth / width;
        const scaleY = pageHeight / height;
        const scale = Math.min(scaleX, scaleY);

        // Calculate dimensions of the scaled image
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        // Calculate positioning to center the image on the page
        const x = (pageWidth - scaledWidth) / 2;
        const y = (pageHeight - scaledHeight) / 2;

        page.drawImage(image, {
          x,
          y,
          width: scaledWidth,
          height: scaledHeight,
        });
      } else if (file.startsWith("data:image/png;base64,")) {
        // Convert base64 encoded PNG image to bytes
        const base64Image = file.replace("data:image/png;base64,", "");
        const imageBytes = Uint8Array.from(atob(base64Image), (c) =>
          c.charCodeAt(0)
        );

        // Embed PNG image into PDF
        const image = await pdfDoc.embedPng(imageBytes);
        const page = pdfDoc.addPage();
        const { width, height } = image.scale(1); // Get original image dimensions
        const pageWidth = page.getWidth();
        const pageHeight = page.getHeight();

        // Calculate scaling factor to fit image proportionally within the page
        const scaleX = pageWidth / width;
        const scaleY = pageHeight / height;
        const scale = Math.min(scaleX, scaleY);

        // Calculate dimensions of the scaled image
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        // Calculate positioning to center the image on the page
        const x = (pageWidth - scaledWidth) / 2;
        const y = (pageHeight - scaledHeight) / 2;

        page.drawImage(image, {
          x,
          y,
          width: scaledWidth,
          height: scaledHeight,
        });
      } else if (file.startsWith("blob:")) {
        try {
          const response = await fetch(file);
          if (response.ok) {
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const imageBytes = new Uint8Array(arrayBuffer);
            const mimeType = blob.type;

            let image;
            if (mimeType === "image/jpeg") {
              image = await pdfDoc.embedJpg(imageBytes);
            } else if (mimeType === "image/png") {
              image = await pdfDoc.embedPng(imageBytes);
            } else {
              console.error("Unsupported blob type:", mimeType);
              continue; // Skip unsupported blob types
            }

            const page = pdfDoc.addPage();
            const { width, height } = image.scale(1);
            const pageWidth = page.getWidth();
            const pageHeight = page.getHeight();

            const scaleX = pageWidth / width;
            const scaleY = pageHeight / height;
            const scale = Math.min(scaleX, scaleY);

            const scaledWidth = width * scale;
            const scaledHeight = height * scale;

            const x = (pageWidth - scaledWidth) / 2;
            const y = (pageHeight - scaledHeight) / 2;

            page.drawImage(image, {
              x,
              y,
              width: scaledWidth,
              height: scaledHeight,
            });
          } else {
            console.error(
              "Failed to fetch blob:",
              response.status,
              response.statusText
            );
          }
        } catch (error) {
          console.error("Error fetching blob:", error);
        }
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const file = new File([blob], "appointment.pdf", {
      type: "application/pdf",
    });
    setUrl(file);
  };

  const getProcedureClass = async (state) => {
    setPcLoadState(state || "get");
    try {
      const res = await apiRequest(
        baseUrl + `/medical-test/procedures?cardNo=${selectedPolicy.cardNo}&searchKey=`,
        "GET"
      );
      if(!res.error){
        setProcedureClass(res.data || []);
        setPcLoadState("get success");
      } else if (res.status >= 500) {
        setPcLoadState("ISE");
      } else {
        setPcLoadState("get failed");
      }
    } catch (error) {
      console.log(error);
      setPcLoadState("timeout");
    }
  };

  const getOrders = async (state) => {
    try {
      setLoadState(state || "get orders");
      const res = await apiRequest(
        baseUrl + `/medical-test/orders?cardNo=${selectedPolicy.cardNo}`,
        "GET"
      );
      if(!res.error){
        setDiagnosticOrders(res.data || []);
        setLoadState("get orders success");
      } else if (res.status >= 500) {
        setLoadState("get orders ISE");
      } else {
        setLoadState("get orders failed");
      }
    } catch (error) {
      setLoadState("get orders timeout");
    }
  };

  const checkProcedureNames=(keywords, names)=>{
    return keywords.every((keyword) =>
      names.some((name) => name.startsWith(keyword))
    );
  }

  const getFilteredClinics=(keywords, clinics)=>{
    return Object.values(clinics).filter(
      (clinic) => {
        const procedureNames = clinic.procedures.map((proc) =>
          proc.procedureName.toUpperCase()
        );
        return checkProcedureNames(keywords, procedureNames);
      }
    );
  }

  const getCleanedData=(clinics)=>{
    return clinics.map((item) => {
      const proc = item.procedures.map((data) => {
        const colonIndex = data.procedureName.indexOf(":");
        if (colonIndex !== -1) {
          return {
            ...data,
            procedureName: data.procedureName.slice(0, colonIndex),
          };
        }
        return data;
      });
      return { ...item, procedures: proc };
    });
  }

  const checkProcedure=(data, selected)=>{
    return selected.coveredProcedures.some((proc) =>
      data.procedures.some((procData) => {
        const selectedClassName = proc.class
          .slice(0, proc.class.indexOf(" "))
          .toUpperCase();
        const procDataClassName = procData.procedureName
          .slice(0, procData.procedureName.indexOf(" "))
          .toUpperCase();
        return proc.subclass !== null
          ? proc.subclass
              .toUpperCase()
              .includes(procData.procedureName.toUpperCase())
          : selectedClassName === procDataClassName ||
              selectedClassName.includes(procDataClassName);
      })
    )
  }

  const getFilteredData=(cleanedData)=>{
    return cleanedData.filter((data) =>
      selectedProcedures.every((selectedProc) =>
        checkProcedure(data, selectedProc)
      )
    );
  }

  const getProviders = async (state) => {

    let procedureClasses = [
      ...new Set(
        selectedProcedures
          .filter((data) => data.coveredProcedures.length > 0)
          .map((data) => data.name)
      ),
    ]

    if(files.length > 0){
      procedureClasses = selectedProcedureClass.map((data) => data.name);
    }

    let processedClinics = [];
    let aggregatedClinics = {};

    try {
      setLoadState(state || "get providers");
      const res = await apiRequest(
        `${baseUrl}/medical-test/providers`,
        "POST",
        {
          ProcedureClasses: procedureClasses
        }
      );
      if(!res.error){

        const clinics = res.data || [];

        if (files.length > 0) {
          const selectedSubclasses = selectedProcedureClass.flatMap(
            (category) => {
              const slicedName = category.name
                .slice(category.name.indexOf(":") + 2)
                .toUpperCase();
              if (slicedName === "12 - L ECG") {
                return "ECG";
              }
              if (slicedName === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
                return "2D ECHO (PEDIA)";
              }
              if (slicedName === "2-D ECHO WITH DOPPLER STUDIES") {
                return "2D ECHO";
              }
              return slicedName.replace(" PROCEDURES", "");
            }
          );

          processedClinics = clinics.filter((clinic) => {
            return selectedSubclasses.some((subclassFromSelected) =>
              clinic.procedureName.toUpperCase().startsWith(subclassFromSelected)
            );
          });

          processedClinics.forEach((clinic) => {
            const { providerName, calendarId, id, procedureName, ...rest } =
              clinic;
            if (aggregatedClinics[providerName]) {
              aggregatedClinics[providerName].procedures.push({
                calendarId,
                id,
                procedureName,
              });
            } else {
              aggregatedClinics[providerName] = {
                ...rest,
                providerName,
                procedures: [{ calendarId, id, procedureName }],
              };
            }
          });

          const keywords = [
            ...new Set(
              selectedProcedureClass.map((procedure) => {
                const slicedName = procedure.name
                  .slice(procedure.name.indexOf(":") + 2)
                  .toUpperCase();
                if (slicedName === "12 - L ECG") {
                  return "ECG";
                }
                if (slicedName === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
                  return "2D ECHO (PEDIA)";
                }
                if (slicedName === "2-D ECHO WITH DOPPLER STUDIES") {
                  return "2D ECHO";
                }
                return slicedName.replace(" PROCEDURES", "");
              })
            ),
          ];

          const filteredClinics = getFilteredClinics(keywords, aggregatedClinics);

          setAvailableClinics(filteredClinics);
        } else {
          const selectedSubclasses = selectedProcedures.flatMap((category) =>
            category.coveredProcedures.map((procedure) => procedure.subclass)
          );

          processedClinics = clinics.filter((clinic) => {
            const { procedureName } = clinic;
            return selectedSubclasses.includes(procedureName);
          });

          const excludedProcedureNames = [
            "Ultrasound (special procedures)",
            "Ultrasound (Breast)",
            "Ultrasound (GENERAL ULTRASOUND)",
          ];
          const remainingClinics = clinics.filter(
            (clinic) => !excludedProcedureNames.includes(clinic.procedureName)
          );

          processedClinics = processedClinics.concat(remainingClinics);

          processedClinics.forEach((clinic) => {
            const { providerName, calendarId, id, procedureName, ...rest } =
              clinic;
            if (aggregatedClinics[providerName]) {
              aggregatedClinics[providerName].procedures.push({
                calendarId,
                id,
                procedureName,
              });
            } else {
              aggregatedClinics[providerName] = {
                ...rest,
                providerName,
                procedures: [{ calendarId, id, procedureName }],
              };
            }
          });

          const keywords = [
            ...new Set(
              selectedProcedures.map((procedure) =>
                procedure.name.split(":")[1].trim().split(" ")[0].toUpperCase()
              )
            ),
          ];

          const filteredClinics = getFilteredClinics(keywords, aggregatedClinics);

          const cleanedData = getCleanedData(filteredClinics);

          const filtered = getFilteredData(cleanedData);

          setAvailableClinics(filtered);
        }
        setLoadState("get providers success");
      } else if (res.status >= 500) {
        setLoadState("get providers ISE");
      } else {
        setLoadState("get providers failed");
      }
    } catch (error) {
      setLoadState("get providers timeout");
    }
  }

  const data = useMemo(
    () => ({
      setSearchProceduresResults,
      setSelectedProcedureClass,
      setSearchBranchResults,
      setDisplaySummaryAlert,
      convertToStandardTime,
      setSelectedProcedures,
      setDiagnosticOrders,
      setMappedProcedures,
      setAvailableClinics,
      setSearchProcedures,
      getProcedureClass,
      setSelectedPolicy,
      setSelectedOrders,
      setProcedureClass,
      setSelectedClinic,
      setAvailableDates,
      setAvailableTimes,
      setSearchBranch,
      setSelectedTimes,
      setSelectedDates,
      handleFileChange,
      setDisplayAlert,
      setPcLoadState,
      getProviders,
      setIsLoading,
      handleCancel,
      compileToPDF,
      setLoadState,
      handleBack,
      removeFile,
      pdfToImage,
      setSummary,
      getOrders,
      navigate,
      setFiles,
      setMonth,
      setYear,
      setUrl,

      searchProceduresResults,
      selectedProcedureClass,
      displaySummaryAlert,
      searchBranchResults,
      selectedProcedures,
      diagnosticOrders,
      mappedProcedures,
      availableClinics,
      searchProcedures,
      selectedPolicy,
      selectedOrders,
      procedureClass,
      availableTimes,
      availableDates,
      selectedClinic,
      selectedTimes,
      selectedDates,
      displayAlert,
      searchBranch,
      pcLoadState,
      isLoading,
      loadState,
      summary,
      month,
      files,
      year,
      url,
    }),
    [
      searchProceduresResults,
      selectedProcedureClass,
      searchBranchResults,
      displaySummaryAlert,
      selectedProcedures,
      diagnosticOrders,
      mappedProcedures,
      availableClinics,
      searchProcedures,
      selectedPolicy,
      selectedOrders,
      procedureClass,
      selectedClinic,
      availableDates,
      availableTimes,
      selectedTimes,
      selectedDates,
      displayAlert,
      searchBranch,
      pcLoadState,
      isLoading,
      loadState,
      summary,
      month,
      files,
      year,
      url,
    ]
  );

  return (
    <RequestMedicalContext.Provider value={data}>
      {props.children}
      <CancelBookingModal
        open={displayAlert}
        onClose={() => setDisplayAlert(false)}
        onCancel={handleCancel}
        title={"medical test"}
      />
    </RequestMedicalContext.Provider>
  );
}
RequestMedicalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
